/**
 * Delete Provider Confirmation Dialog
 *
 * Confirmation dialog before soft-deleting a provider.
 * Calls DELETE /api/providers/<uuid>/delete/ via useProviderActions.
 */

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { AlertCircle } from 'lucide-react'
import type { OrganizationProviderRow } from '@/hooks/useOrganizationProviders'
import { useLocalization } from '@/contexts/LocalizationContext'

interface DeleteProviderDialogProps {
  provider: OrganizationProviderRow | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (providerId: string) => Promise<void>
  isLoading?: boolean
}

export function DeleteProviderDialog({
  provider,
  open,
  onOpenChange,
  onConfirm,
  isLoading = false,
}: DeleteProviderDialogProps) {
  const { t } = useLocalization()
  
  const handleDelete = async () => {
    if (!provider?.id) return
    await onConfirm(provider.id)
    onOpenChange(false)
  }

  if (!provider) return null

  const displayName = provider.fullName || provider.name || t('providers.thisProvider')

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <AlertCircle className="h-5 w-5" />
            {t('providers.deleteProvider')}
          </DialogTitle>
          <DialogDescription>
            {t('providers.deleteProviderWarning')}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="rounded-lg border border-red-200 bg-red-50 p-4">
            <p className="text-sm text-gray-700">
              {t('providers.deleteConfirm', { name: displayName })}
            </p>
            {provider.activeServicesCount != null &&
              provider.activeServicesCount > 0 && (
                <p className="mt-2 text-sm text-red-600 font-medium">
                  {t('providers.deleteActiveWarning', { count: provider.activeServicesCount })}
                </p>
              )}
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={isLoading}
          >
            {isLoading ? t('common.deleting') : t('providers.deleteProvider')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
